import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import "./NewReleaseSong.css";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { songImageUrl, extractObjectId } from "../../../utils/utils";
import MultiGenreSelect from "../../../components/common/MultiGenreSelect";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import {
  FaMusic,
  FaSearch,
  FaUser,
  FaCompactDisc,
  FaPlay,
  FaEye,
  FaTrash,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaRocket
} from "react-icons/fa";

const NewReleaseSong = () => {
  const navigate = useNavigate();
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;
  const isPageTargetReady =
    !pageTarget.isLoadingTargets && Boolean(pageTarget.selectedTarget);
  const targetRequestKey = useMemo(
    () => `${targetParams.pageType}:${targetParams.genreSlug || ""}`,
    [targetParams.pageType, targetParams.genreSlug]
  );
  const latestTargetKeyRef = useRef(targetRequestKey);
  const listRequestRef = useRef(0);
  const searchRequestRef = useRef(0);
  const [newReleaseSongs, setNewReleaseSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchResults, setSearchResults] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [expandedGenres, setExpandedGenres] = useState({});
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [automationControl, setAutomationControl] = useState(null);
  const [automationLoading, setAutomationLoading] = useState(true);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [automationRunning, setAutomationRunning] = useState(false);

  latestTargetKeyRef.current = targetRequestKey;

  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres(
        { page: 1, limit: 1000 },
        getAuthConfig()
      );
      if (response?.success && Array.isArray(response.data)) {
        setGenres(response.data);
      }
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  const fetchAutomatedReleaseControl = async () => {
    setAutomationLoading(true);
    try {
      const response = await allAPiServicesCall.getAutomatedReleaseControl(
        getAuthConfig()
      );
      const control =
        response?.data && typeof response.data.enabled === "boolean"
          ? response.data
          : response;
      if (control && typeof control.enabled === "boolean") {
        setAutomationControl(control);
      }
    } finally {
      setAutomationLoading(false);
    }
  };

  const updateAutomationField = (field, value) => {
    setAutomationControl((current) => ({
      ...(current || {}),
      [field]: value,
    }));
  };

  const saveAutomatedReleaseControl = async () => {
    if (!automationControl) return;
    setAutomationSaving(true);
    try {
      const response = await allAPiServicesCall.updateAutomatedReleaseControl(
        {
          enabled: automationControl.enabled === true,
          lookbackDays: Number(automationControl.lookbackDays),
          songLimit: Number(automationControl.songLimit),
          albumLimit: Number(automationControl.albumLimit),
          maxSongsPerArtist: Number(automationControl.maxSongsPerArtist),
          maxAlbumsPerArtist: Number(automationControl.maxAlbumsPerArtist),
        },
        getAuthConfig()
      );
      const control =
        response?.data && typeof response.data.enabled === "boolean"
          ? response.data
          : response;
      if (control && typeof control.enabled === "boolean") {
        setAutomationControl(control);
        toast.success("Automated release settings saved");
      }
    } finally {
      setAutomationSaving(false);
    }
  };

  const runAutomatedReleasePublication = async (dryRun) => {
    setAutomationRunning(true);
    try {
      const response = await allAPiServicesCall.runAutomatedReleasePublication(
        { dryRun },
        getAuthConfig()
      );
      const report =
        response?.data && response.data.status ? response.data : response;
      if (report?.status === "SUCCEEDED") {
        toast.success(
          dryRun
            ? "Dry run completed. No public snapshot was changed."
            : "New release snapshot published."
        );
      } else if (report?.status === "SKIPPED") {
        toast.info(report.reason || "Publication was skipped.");
      } else if (report) {
        toast.error(report.error || "Automated release publication failed.");
      }
      await fetchAutomatedReleaseControl();
    } finally {
      setAutomationRunning(false);
    }
  };

  const getLastRunDescription = () => {
    const lastRun = automationControl?.lastRun;
    if (!lastRun) return "No publication has run yet.";
    const when = lastRun.finishedAt
      ? new Date(lastRun.finishedAt).toLocaleString()
      : "in progress";
    const songs = lastRun.publishedCounts?.songs ?? 0;
    const albums = lastRun.publishedCounts?.albums ?? 0;
    return (
      when +
      " · " +
      (lastRun.status || "UNKNOWN") +
      " · " +
      songs +
      " songs, " +
      albums +
      " albums"
    );
  };

  useEffect(() => {
    fetchGenres();
    fetchAutomatedReleaseControl();
  }, []);

  const fetchNewReleaseSongList = async (
    page = 1,
    requestTargetParams = targetParams,
    requestTargetKey = targetRequestKey
  ) => {
    if (!isPageTargetReady) return;

    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getNewReleaseSong(
        { page, limit: 10, ...requestTargetParams },
        getAuthConfig()
      );

      if (
        requestId !== listRequestRef.current ||
        requestTargetKey !== latestTargetKeyRef.current
      ) {
        return;
      }

      const { success, data } = response;
      if (success && Array.isArray(data.newReleaseList)) {
        setNewReleaseSongs(data.newReleaseList);
        setTotalPages(data.totalPages || 1);
        setTotalCount(data.totalCount || data.newReleaseList.length);
      } else {
        console.warn("No data found or data is not an array");
      }
    } catch (error) {
      console.error("Error fetching new release songs:", error);
    } finally {
      if (requestId === listRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchSearchSong = async (song_name) => {
    if (!song_name.trim()) return;
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getSearchSong(
        { song_name },
        getAuthConfig()
      );
      if (requestId !== searchRequestRef.current) {
        return;
      }
      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
    } catch (error) {
      console.error("Error fetching search song:", error);
      toast.error("Failed to fetch search song");
    } finally {
      if (requestId === searchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const addNewReleaseSong = async (song_id) => {
    const genreArray = selectedGenres[song_id] || [];
    const genre = genreArray.length > 0 ? genreArray.join(", ") : null;
    setLoading(true);
    try {
      await allAPiServicesCall.addNewReleaseSong(
        song_id,
        getAuthConfig(),
        targetParams,
        genre
      );
      toast.success("Song added to New Releases!");
      setSearchTerm("");
      setSelectedGenres({});
      fetchNewReleaseSongList(currentPage);
    } catch (error) {
      toast.error("Failed to add song.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenreSelect = (songId, genreArray) => {
    setSelectedGenres(prev => ({
      ...prev,
      [songId]: genreArray
    }));
  };

  const handleOnDelete = async () => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.deleteNewReleaseSong(
        deletingId,
        getAuthConfig()
      );

      if (response && response.status === 200) {
        toast.success("Song Removed From New Release Songs")
        setShowDeleteModal(false);
        fetchNewReleaseSongList(currentPage);
      } else {
        toast.error("Failed to delete song!");
      }
    } catch (error) {
      toast.error("Failed to delete song!");
    } finally {
      setLoading(false);
      setDeletingId(null);
    }
  };

  const confirmDelete = (songId) => {
    setDeletingId(songId);
    setShowDeleteModal(true);
  };

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    if (!isPageTargetReady) return;

    listRequestRef.current += 1;
    searchRequestRef.current += 1;
    setNewReleaseSongs([]);
    setSearchResults([]);
    setTotalCount(0);
    setTotalPages(1);
    setCurrentPage(1);
  }, [targetRequestKey, isPageTargetReady]);

  useEffect(() => {
    if (!isPageTargetReady) return;

    if (!searchTerm.trim()) {
      fetchNewReleaseSongList(currentPage, targetParams, targetRequestKey);
      setSearchResults([]);
    }
  }, [
    currentPage,
    searchTerm,
    targetParams.pageType,
    targetParams.genreSlug,
    targetRequestKey,
    isPageTargetReady,
  ]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      searchRequestRef.current += 1;
      setDebouncedTerm("");
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedTerm) {
      fetchSearchSong(debouncedTerm);
    }
  }, [debouncedTerm]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  return (
    <CommonLayout>
      <div className="new-release-page">
        {/* Header Section */}
        <div className="new-release-header">
          <div className="new-release-header-content">
            <div className="new-release-header-icon">
              <FaRocket />
            </div>
            <div className="new-release-header-text">
              <h1 className="new-release-title">Automated New Releases</h1>
              <p className="new-release-subtitle">
                Automatically publish recent songs and albums, ranked by artist views
              </p>
            </div>
          </div>
          <div className="new-release-stats">
            <div className="new-release-stat-card">
              <FaMusic className="new-release-stat-icon" />
              <div className="new-release-stat-info">
                <span className="new-release-stat-value">{totalCount}</span>
                <span className="new-release-stat-label">Legacy Manual Songs</span>
              </div>
            </div>
          </div>
        </div>

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h2 className="h4 mb-0">Automatic weekly publication</h2>
                  <Badge bg={automationControl?.enabled ? "success" : "secondary"}>
                    {automationControl?.enabled ? "Enabled" : "Paused"}
                  </Badge>
                </div>
                <p className="text-muted mb-0 mt-1">
                  Homepage and genre pages show only the latest successful snapshot,
                  ordered strictly by the artist&apos;s site views.
                </p>
              </div>
              <small className="text-muted">
                {automationLoading ? "Loading settings…" : getLastRunDescription()}
              </small>
            </div>

            <Alert variant="info" className="py-2">
              Song and album popularity is never used for this order. The weekly
              job first completes successfully, then publishes one immutable snapshot.
            </Alert>

            <Form>
              <Form.Check
                type="switch"
                id="automated-release-enabled"
                className="mb-3"
                label="Enable automatic publication after the weekly catalog update"
                checked={automationControl?.enabled === true}
                disabled={automationLoading || automationSaving}
                onChange={(event) =>
                  updateAutomationField("enabled", event.target.checked)
                }
              />
              <Row className="g-3">
                <Col sm={6} lg={2}>
                  <Form.Group>
                    <Form.Label>Lookback days</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="31"
                      value={automationControl?.lookbackDays ?? ""}
                      disabled={automationLoading || automationSaving}
                      onChange={(event) =>
                        updateAutomationField("lookbackDays", event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={6} lg={2}>
                  <Form.Group>
                    <Form.Label>Song slots</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="24"
                      value={automationControl?.songLimit ?? ""}
                      disabled={automationLoading || automationSaving}
                      onChange={(event) =>
                        updateAutomationField("songLimit", event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={6} lg={2}>
                  <Form.Group>
                    <Form.Label>Album slots</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="12"
                      value={automationControl?.albumLimit ?? ""}
                      disabled={automationLoading || automationSaving}
                      onChange={(event) =>
                        updateAutomationField("albumLimit", event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={6} lg={3}>
                  <Form.Group>
                    <Form.Label>Max songs per artist</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="5"
                      value={automationControl?.maxSongsPerArtist ?? ""}
                      disabled={automationLoading || automationSaving}
                      onChange={(event) =>
                        updateAutomationField(
                          "maxSongsPerArtist",
                          event.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={6} lg={3}>
                  <Form.Group>
                    <Form.Label>Max albums per artist</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="3"
                      value={automationControl?.maxAlbumsPerArtist ?? ""}
                      disabled={automationLoading || automationSaving}
                      onChange={(event) =>
                        updateAutomationField(
                          "maxAlbumsPerArtist",
                          event.target.value
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button
                  variant="primary"
                  onClick={saveAutomatedReleaseControl}
                  disabled={automationLoading || automationSaving || !automationControl}
                >
                  {automationSaving ? "Saving…" : "Save settings"}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => runAutomatedReleasePublication(true)}
                  disabled={automationLoading || automationRunning}
                >
                  {automationRunning ? "Running…" : "Dry run"}
                </Button>
                <Button
                  variant="success"
                  onClick={() => runAutomatedReleasePublication(false)}
                  disabled={automationLoading || automationRunning}
                >
                  Publish now
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="This selector applies only to the legacy manual song list below. Automated releases are calculated for every homepage and genre."
        />

        {/* Search Controls */}
        <div className="new-release-controls">
          <div className="new-release-search-wrapper">
            <FaSearch className="new-release-search-icon" />
            <input
              type="text"
              className="new-release-search-input"
              placeholder="Search for a song to add to the legacy manual list..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </div>
        </div>

        {/* Content */}
        {pageTarget.isLoadingTargets || loading ? (
          <div className="new-release-loading">
            <div className="new-release-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="new-release-loading-text">
              {pageTarget.isLoadingTargets
                ? "Loading page target..."
                : "Loading songs..."}
            </p>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            {/* Search Results Section */}
            <div className="new-release-section-header">
              <div className="new-release-section-icon new-release-section-icon-search">
                <FaSearch />
              </div>
              <h2 className="new-release-section-title">Search Results</h2>
              <span className="new-release-section-count">
                {searchResults.length} found
              </span>
            </div>
            <div className="new-release-grid">
              {searchResults.map((song, index) => (
                <div
                  key={index}
                  className="new-release-card new-release-card-search"
                >
                  <div className="new-release-card-image-wrapper">
                    <img
                      src={songImageUrl(song)}
                      alt={song.song_name}
                      className="new-release-card-image"
                    />
                    <div className="new-release-card-overlay">
                      <div className="new-release-card-play">
                        <FaPlay />
                      </div>
                    </div>
                    <div className="new-release-card-badge new-release-card-badge-search">
                      <FaSearch /> Result
                    </div>
                  </div>
                  <div className="new-release-card-content">
                    <h3 className="new-release-card-title">{song.song_name}</h3>
                    <div className="new-release-card-meta">
                      <div className="new-release-card-meta-item">
                        <FaUser className="new-release-card-meta-icon" />
                        <span className="new-release-card-meta-value">
                          {song?.artist?.artist_name || "Unknown Artist"}
                        </span>
                      </div>
                    </div>
                    <div className="new-release-genre-select">
                      <MultiGenreSelect
                        genres={genres}
                        selectedGenres={selectedGenres[song.song_id] || []}
                        onChange={(genreArray) =>
                          handleGenreSelect(song.song_id, genreArray)
                        }
                        placeholder="Select Genres (Optional)"
                        size="sm"
                      />
                    </div>
                    <div className="new-release-card-actions">
                      <button
                        className="new-release-action-btn new-release-action-btn-add"
                        onClick={() => addNewReleaseSong(song.song_id)}
                      >
                        <FaPlus /> Add to New Release
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : newReleaseSongs.length === 0 ? (
          <div className="new-release-empty">
            <div className="new-release-empty-icon">
              <FaMusic />
            </div>
            <h3 className="new-release-empty-title">No Legacy Manual Songs</h3>
            <p className="new-release-empty-text">
              The automatic snapshot is managed above. Use search only for the legacy manual list.
            </p>
          </div>
        ) : (
          <>
            {/* Legacy manual songs grid — it never mixes into the automatic snapshot. */}
            <div className="new-release-grid">
              {newReleaseSongs
                .filter((song) =>
                  song.song_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                )
                .map((song, index) => (
                  <div key={index} className="new-release-card">
                    <div className="new-release-card-image-wrapper">
                      <img
                        src={songImageUrl(song)}
                        alt={song.song_name}
                        className="new-release-card-image"
                      />
                      <div className="new-release-card-overlay">
                        <div className="new-release-card-play">
                          <FaPlay />
                        </div>
                      </div>
                      <div className="new-release-card-badge">
                        <FaRocket /> New
                      </div>
                    </div>
                    <div className="new-release-card-content">
                      <h3 className="new-release-card-title">
                        {song.song_name}
                      </h3>
                      <div className="new-release-card-meta">
                        <div className="new-release-card-meta-item">
                          <FaUser className="new-release-card-meta-icon" />
                          <span className="new-release-card-meta-value">
                            {song.artist?.artist_name || "Unknown Artist"}
                          </span>
                        </div>
                        <div className="new-release-card-meta-item">
                          <FaCompactDisc className="new-release-card-meta-icon" />
                          <span className="new-release-card-meta-value">
                            {song.album?.album_name || "No Album"}
                          </span>
                        </div>
                      </div>
                      {song.genre && (() => {
                        const genreList = song.genre.split(", ").map(g => g.trim());
                        const isExpanded = expandedGenres[song._id];
                        const visibleGenres = isExpanded ? genreList : genreList.slice(0, 4);
                        const hasMore = genreList.length > 4;

                        return (
                          <div className="new-release-card-genres">
                            {visibleGenres.map((g, i) => (
                              <span key={i} className="new-release-card-genre">
                                {g}
                              </span>
                            ))}
                            {hasMore && (
                              <button
                                className="new-release-genre-toggle"
                                onClick={() => setExpandedGenres(prev => ({
                                  ...prev,
                                  [song._id]: !prev[song._id]
                                }))}
                              >
                                {isExpanded ? "Show less" : `+${genreList.length - 4} more`}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="new-release-card-actions">
                        <button
                          className="new-release-action-btn new-release-action-btn-view"
                          onClick={() => navigate(`/lyrics/${song.song_id}`)}
                        >
                          <FaEye /> View
                        </button>
                        <button
                          className="new-release-action-btn new-release-action-btn-delete"
                          onClick={() => confirmDelete(extractObjectId(song._id))}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="new-release-pagination">
                <button
                  className="new-release-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="new-release-pagination-info">
                  <span className="new-release-pagination-current">
                    {currentPage}
                  </span>
                  <span className="new-release-pagination-separator">/</span>
                  <span className="new-release-pagination-total">
                    {totalPages}
                  </span>
                </div>
                <button
                  className="new-release-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to remove this song from new releases?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleOnDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </CommonLayout>
  );
};

export default NewReleaseSong;
